import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const formSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  description: z.string().optional(),
  display_order: z.coerce.number().int().min(1, { message: 'Display order must be at least 1' }),
  is_active: z.boolean().default(true),
  state_code: z.string().min(2, { message: 'State code is required' }),
  retention_years: z.coerce.number().int().min(1, { message: 'Retention period must be at least 1 year' }).default(5),
  reporting_format: z.string().default('ASCII'),
});

type FormValues = z.infer<typeof formSchema>;

interface CertificateType {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  state_code?: string;
  retention_years?: number;
  reporting_format?: string;
  compliance_info?: any;
}

interface EditCertificateTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  certificateType: CertificateType | null;
  onSuccess?: () => void;
}

export function EditCertificateTypeDialog({ 
  open, 
  onOpenChange, 
  certificateType, 
  onSuccess 
}: EditCertificateTypeDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: certificateType?.name || '',
      description: certificateType?.description || '',
      display_order: certificateType?.display_order || 1,
      is_active: certificateType?.is_active || true,
      state_code: certificateType?.state_code || 'FL',
      retention_years: certificateType?.retention_years || 5,
      reporting_format: certificateType?.reporting_format || 'ASCII',
    },
  });

  // Update form values when certificateType changes
  React.useEffect(() => {
    if (certificateType) {
      form.reset({
        name: certificateType.name,
        description: certificateType.description || '',
        display_order: certificateType.display_order,
        is_active: certificateType.is_active,
        state_code: certificateType.state_code || 'FL',
        retention_years: certificateType.retention_years || 5,
        reporting_format: certificateType.reporting_format || 'ASCII',
      });
    }
  }, [certificateType, form]);

  async function onSubmit(values: FormValues) {
    if (!certificateType) return;
    
    try {
      const { data, error } = await supabase
        .from('certificate_types')
        .update({
          name: values.name,
          description: values.description || null,
          display_order: values.display_order,
          is_active: values.is_active,
          compliance_info: {
            state_code: values.state_code,
            retention_years: values.retention_years,
            reporting_format: values.reporting_format
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', certificateType.id)
        .select()
        .single();
      
      if (error) {
        toast.error("Error", error.message);
        return;
      }
      
      toast.success("Success", "Certificate type updated successfully");
      
      // Reset form and close dialog
      form.reset();
      onOpenChange(false);
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating certificate type:', error);
      toast.error("Error", "An unexpected error occurred");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Certificate Type</DialogTitle>
          <DialogDescription>
            Update the details of this certificate type.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="E.g., BDI Certificate" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Description of the certificate type" 
                      {...field} 
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="state_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="FL">Florida (FL)</SelectItem>
                        <SelectItem value="CA">California (CA)</SelectItem>
                        <SelectItem value="TX">Texas (TX)</SelectItem>
                        <SelectItem value="NY">New York (NY)</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="retention_years"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Retention (years)</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" max="10" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Record retention period
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="reporting_format"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reporting Format</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ASCII">ASCII (FLHSMV)</SelectItem>
                      <SelectItem value="CSV">CSV</SelectItem>
                      <SelectItem value="JSON">JSON</SelectItem>
                      <SelectItem value="PDF">PDF</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-xs">
                    Required format for state reporting
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="display_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Order</FormLabel>
                  <FormControl>
                    <Input type="number" min="1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active Status</FormLabel>
                    <FormDescription className="text-sm text-muted-foreground">
                      Set whether this certificate type is active
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">Update Certificate Type</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
